import { ArrowLeft, ArrowRight } from '@icon';
import classNames from 'classnames';
import React from 'react';
import { HearthBeatLoader } from '../loaders';
import Typography from '../typography';
import { ButtonContentSC, ButtonSC, ButtonTextContentSC } from './button.styled';
import { ButtonProps } from './button.types';
import { coreSizes, iconSize, loaderStyle, useButtonStyle } from './button.utils';

const Button = (props: ButtonProps, ref?: React.Ref<HTMLButtonElement>) => {
  const {
    children,
    variant = 'primary',
    leftArrow = false,
    rightArrow = false,
    Icon,
    loading,
    disabled = false,
    size = 'large',
    block = true,
    style,
    type = 'button',
    className,
    ...rest
  } = props;

  const styleConfig = useButtonStyle({ variant, size });

  const coreComponentSize = React.useCallback(
    (icon: boolean) => {
      const sizes = coreSizes[size];
      const check = block ? leftArrow || rightArrow : icon;
      return check ? sizes.component : sizes.padding;
    },
    [size, block, leftArrow, rightArrow],
  );

  const componentPadding = size === 'large' || size === 'medium' ? 8 : 4;
  const leftSize = coreComponentSize(leftArrow);
  const rightSize = coreComponentSize(rightArrow);

  return (
    <ButtonSC
      {...rest}
      ref={ref}
      $variant={variant}
      $size={size}
      $block={block}
      $color={styleConfig.color}
      $backgroundColor={styleConfig.backgroundColor}
      style={style}
      type={type}
      disabled={disabled}
      className={classNames('ui-button', className)}
    >
      <ButtonContentSC $loading={loading}>
        <div
          style={{
            width: leftSize,
            minWidth: leftSize,
            paddingLeft: componentPadding,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {leftArrow && <ArrowLeft {...iconSize[size]} color={styleConfig.color} />}
        </div>
        <ButtonTextContentSC>
          {Icon ? (
            <Icon
              {...iconSize[size]}
              color={styleConfig.color}
              style={{ marginRight: componentPadding }}
            />
          ) : null}
          <Typography
            style={{
              color: styleConfig.color,
            }}
            {...styleConfig.textConfig}
            noWrap
          >
            {children}
          </Typography>
        </ButtonTextContentSC>
        <div
          style={{
            width: rightSize,
            minWidth: rightSize,
            paddingRight: componentPadding,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {rightArrow && <ArrowRight {...iconSize[size]} color={styleConfig.color} />}
        </div>
      </ButtonContentSC>
      {loading ? (
        <div className="loader">
          <HearthBeatLoader
            loading
            size={size === 'large' ? 'medium' : 'small'}
            variant={loaderStyle[variant]}
          />
        </div>
      ) : null}
    </ButtonSC>
  );
};

export default React.forwardRef(Button);
